import { Client } from '@colyseus/sdk'
import type { Room } from '@colyseus/sdk'
import {
  CLOSE_KICKED,
  MSG_CODE,
  MSG_EMOTE,
  MSG_EVENTS,
  MSG_INPUT,
  MSG_LAG,
  MSG_REWARD,
  MSG_READY,
  MSG_REMATCH,
  MSG_START,
  RaceState,
} from '@runkr/schema'
import type {
  CodeMsg,
  EmoteBroadcast,
  RewardMsg,
  EmoteMsg,
  EventsMsg,
  InputMsg,
  LagMsg,
  RaceJoinOptions,
  RacePhase,
  SeatReservation,
} from '@runkr/schema'
import { backoffMs } from './backoff'
import { lookupCode } from './codeLookup'
import { refusalReason } from './leaveReason'
import type { LeaveReason } from './leaveReason'
import { roomSnapshot } from './roomSnapshot'
import type { RoomSnapshot } from './roomSnapshot'
import { Store } from './store'

const ROOM = 'race'
const PING_EVERY_MS = 1000
/** SDK reconnection may start this soon after joining (its default is 5 s). */
const RECONNECT_MIN_UPTIME_MS = 1000

/**
 * `connecting`: joining (or rejoining after a drop outside a race). `joined`: in a room.
 * `reconnecting`: the SDK is resuming the session during a race (D-026). `offline`: waiting to
 * retry with backoff. `left`: no room and no retry (left, kicked, room closed or join refused).
 */
export type NetStatus = 'idle' | 'connecting' | 'joined' | 'reconnecting' | 'offline' | 'left'

export interface NetState {
  status: NetStatus
  /** Why the client is offline or left, null otherwise. */
  error: string | null
  /** Set with status `left` when the client was refused, kicked or lost the race (T1.8). */
  reason: LeaveReason | null
  roomId: string | null
  sessionId: string | null
  /** Private room join code, '' for public rooms. */
  code: string
}

type JoinIntent =
  | { kind: 'public' }
  | { kind: 'private' }
  | { kind: 'code'; code: string; roomId?: string | undefined }
  | { kind: 'seat'; reservation: SeatReservation }

export interface GameClientOptions {
  /** `ws(s)://host:port` of the game server. */
  serverUrl: string
  /** Called before every join so a refreshed token is used on rejoin. */
  getToken: () => Promise<string>
  /** Wanted display name, used by dev-mode servers only. */
  name?: string
  /** Dev aid (`?lag=<ms>`): extra round trip, half on the way out and half on the way in. */
  lagMs?: number
  /** The runner picked in the hub, read at every join (PHASE3 T3.1, D-049). */
  charId?: () => number
  /** The ride equipped in the hub's Rides tab, read at every join (T3.11, D-049). */
  ride?: () => string
}

/** A join that must not be retried (wrong code, race already running, bad token). */
class JoinRefused extends Error {}

type Listener<T> = (value: T) => void

/** Listener set with an unsubscribe function per listener. */
class Hook<T> {
  private readonly set = new Set<Listener<T>>()
  add(l: Listener<T>): () => void {
    this.set.add(l)
    return () => this.set.delete(l)
  }
  emit(v: T): void {
    for (const l of this.set) l(v)
  }
}

/**
 * Network client for `race` rooms (ARCHITECTURE §6, PHASE1 T1.7) on `@colyseus/sdk`. Joins a
 * public room, creates a private one, or joins by code (`GET /code/<CODE>` then `joinById`).
 * Hands every state patch to `onSnapshot` as a plain `RoomSnapshot` and every `"ev"` batch
 * to `onEvent`; measures RTT with the SDK ping once a second. Outside a race a dropped or
 * failed connection rejoins with backoff (Phase 0 behaviour); during countdown and racing the
 * SDK's own reconnection resumes the session (the room calls `allowReconnection`, D-026), and
 * if it gives up the status becomes `left`.
 */
export class GameClient {
  readonly state = new Store<NetState>({
    status: 'idle',
    error: null,
    reason: null,
    roomId: null,
    sessionId: null,
    code: '',
  })
  private readonly snapshots = new Hook<RoomSnapshot>()
  private readonly events = new Hook<EventsMsg>()
  private readonly lags = new Hook<LagMsg>()
  private readonly rtts = new Hook<number>()
  private readonly emotes = new Hook<EmoteBroadcast>()
  private readonly rewards = new Hook<RewardMsg>()
  private readonly client: Client
  private room: Room<unknown, RaceState> | null = null
  private intent: JoinIntent | null = null
  private phase: RacePhase | null = null
  private attempt = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private leaving = false

  constructor(private readonly opts: GameClientOptions) {
    this.client = new Client(opts.serverUrl)
  }

  joinPublic(): void {
    this.begin({ kind: 'public' })
  }

  createPrivate(): void {
    this.begin({ kind: 'private' })
  }

  /** Join a private race by code; `roomId` when the code was already looked up. */
  joinCode(code: string, roomId?: string): void {
    this.begin({ kind: 'code', code: code.trim().toUpperCase(), roomId })
  }

  /** Take the seat a party reserved for us in a public race (T2.7); never retried. */
  joinSeat(reservation: SeatReservation): void {
    this.begin({ kind: 'seat', reservation })
  }

  /**
   * The race join options, built fresh for every join: the one builder for a solo join and a
   * party's seats (T2.7: the party room passes them, minus the token, into each reservation).
   */
  async joinOptions(): Promise<RaceJoinOptions> {
    const options: RaceJoinOptions = { token: await this.opts.getToken() }
    if (this.opts.name) options.name = this.opts.name
    const charId = this.opts.charId?.()
    if (charId !== undefined && charId >= 0) options.charId = charId
    const ride = this.opts.ride?.()
    if (ride) options.ride = ride
    return options
  }

  /** One input per client tick (`"i" {t, b}`). */
  sendInput(t: number, b: number): void {
    const msg: InputMsg = { t, b }
    this.out(() => this.room?.send(MSG_INPUT, msg))
  }

  /** Private room host, while waiting: start the countdown. */
  start(): void {
    this.out(() => this.room?.send(MSG_START))
  }

  ready(): void {
    this.out(() => this.room?.send(MSG_READY))
  }

  /** Show emote `id` to the room (the server allows one per second). */
  emote(id: number): void {
    const msg: EmoteMsg = { id }
    this.out(() => this.room?.send(MSG_EMOTE, msg))
  }

  /** Private room host, after the race: back to waiting with a new seed. */
  rematch(): void {
    this.out(() => this.room?.send(MSG_REMATCH))
  }

  async leave(): Promise<void> {
    this.leaving = true
    this.intent = null
    this.clearTimers()
    const room = this.room
    this.room = null
    if (room) await room.leave(true)
    this.patch({ status: 'left', error: null, reason: null, roomId: null })
  }

  onSnapshot(l: Listener<RoomSnapshot>): () => void {
    return this.snapshots.add(l)
  }

  onEvent(l: Listener<EventsMsg>): () => void {
    return this.events.add(l)
  }

  /** The server's once-a-second input timing report (clock sync). */
  onLag(l: Listener<LagMsg>): () => void {
    return this.lags.add(l)
  }

  /** Emotes relayed by the server, the sender's included. */
  onEmote(l: Listener<EmoteBroadcast>): () => void {
    return this.emotes.add(l)
  }

  /** This player's saved race rewards (`"reward"`, T2.3); the progress UI subscribes in T2.4. */
  onReward(l: Listener<RewardMsg>): () => void {
    return this.rewards.add(l)
  }

  /** Round-trip time samples in ms (including `lagMs`). */
  onRtt(l: Listener<number>): () => void {
    return this.rtts.add(l)
  }

  private begin(intent: JoinIntent): void {
    if (this.room || this.intent) throw new Error('GameClient: already joined or joining')
    this.intent = intent
    this.leaving = false
    this.attempt = 0
    void this.connect()
  }

  private async connect(): Promise<void> {
    this.retryTimer = null
    const intent = this.intent
    if (!intent) return
    this.patch({ status: 'connecting', reason: null })
    try {
      const options = await this.joinOptions()
      const room = await this.joinRoom(intent, options)
      if (this.leaving) {
        void room.leave(true)
        return
      }
      this.attach(room)
    } catch (err) {
      if (this.leaving) return
      console.warn('[net] join failed', err)
      const message = err instanceof Error ? err.message : String(err)
      const refused = err instanceof JoinRefused ? 'not-found' : refusalReason(err)
      // A reservation is single-use: a failed seat is a refusal, not worth a retry.
      const reason = refused ?? (intent.kind === 'seat' ? 'full' : null)
      if (reason) {
        this.intent = null
        this.patch({ status: 'left', error: message, reason })
      } else {
        this.scheduleRetry(message)
      }
    }
  }

  private async joinRoom(
    intent: JoinIntent,
    options: RaceJoinOptions,
  ): Promise<Room<unknown, RaceState>> {
    if (intent.kind === 'public')
      return this.client.joinOrCreate<RaceState>(ROOM, options, RaceState)
    if (intent.kind === 'private') {
      return this.client.create<RaceState>(ROOM, { ...options, private: true }, RaceState)
    }
    if (intent.kind === 'seat') return this.takeSeat(intent.reservation, options.token ?? '')
    const roomId = intent.roomId ?? (await this.lookupCode(intent.code))
    return this.client.joinById<RaceState>(roomId, { ...options, code: intent.code }, RaceState)
  }

  /** `GET /code/<CODE>` (D-025); a party's code is no race to join here (D-040). */
  private async lookupCode(code: string): Promise<string> {
    const found = await lookupCode(this.opts.serverUrl, code)
    if (found?.kind !== 'race') throw new JoinRefused(`no race with code ${code}`)
    return found.roomId
  }

  /** A party's seat (T2.7): no token in it, ours goes as the SDK auth token on connect. */
  private takeSeat(seat: SeatReservation, token: string): Promise<Room<unknown, RaceState>> {
    this.client.auth.token = token
    try {
      return this.client.consumeSeatReservation<RaceState>(seat, RaceState)
    } finally {
      // The SDK builds the connect URL synchronously; later requests must not carry the token.
      this.client.auth.token = ''
    }
  }

  private attach(room: Room<unknown, RaceState>): void {
    this.room = room
    this.attempt = 0
    this.phase = null
    room.reconnection.enabled = false
    room.reconnection.minUptime = RECONNECT_MIN_UPTIME_MS
    this.patch({ status: 'joined', error: null, roomId: room.roomId, sessionId: room.sessionId })

    room.onStateChange((state) => {
      this.phase = state.phase
      // Only the race phases have a reconnect window on the server (§4.4).
      room.reconnection.enabled = state.phase === 'countdown' || state.phase === 'racing'
      if (state.code !== this.state.get().code) this.patch({ code: state.code })
      let snap: RoomSnapshot
      try {
        snap = roomSnapshot(state)
      } catch (err) {
        console.warn('[net] skipped a patch', err)
        return
      }
      this.in(() => this.snapshots.emit(snap))
    })
    room.onMessage(MSG_EVENTS, (batch: EventsMsg) => this.in(() => this.events.emit(batch)))
    room.onMessage(MSG_LAG, (r: LagMsg) => this.in(() => this.lags.emit(r)))
    room.onMessage(MSG_CODE, (m: CodeMsg) => this.patch({ code: m.code }))
    room.onMessage(MSG_EMOTE, (m: EmoteBroadcast) => this.in(() => this.emotes.emit(m)))
    room.onMessage(MSG_REWARD, (m: RewardMsg) => this.in(() => this.rewards.emit(m)))
    room.onDrop(() => this.patch({ status: 'reconnecting' }))
    room.onReconnect(() => this.patch({ status: 'joined', error: null }))
    room.onLeave((code) => this.onLeave(room, code))

    this.ping()
    this.pingTimer = setInterval(() => this.ping(), PING_EVERY_MS)
  }

  private onLeave(room: Room<unknown, RaceState>, code: number): void {
    if (this.room !== room) return
    this.room = null
    this.clearTimers()
    if (this.leaving) return
    const phase = this.phase
    if (code === CLOSE_KICKED) {
      this.intent = null
      this.patch({ status: 'left', error: 'kicked', reason: 'kicked', roomId: null })
    } else if (phase === 'countdown' || phase === 'racing') {
      // The SDK's reconnection gave up: back to the menu (T1.8).
      this.intent = null
      this.patch({ status: 'left', error: 'connection lost', reason: 'lost', roomId: null })
    } else if (phase === 'finished') {
      this.intent = null
      this.patch({ status: 'left', error: null, roomId: null })
    } else {
      // Waiting (or never got state): join again with backoff, like Phase 0.
      this.scheduleRetry(`left (${code})`)
    }
  }

  private scheduleRetry(reason: string): void {
    if (this.retryTimer || !this.intent) return
    this.patch({ status: 'offline', error: reason, roomId: null })
    this.retryTimer = setTimeout(() => void this.connect(), backoffMs(this.attempt++))
  }

  private ping(): void {
    const room = this.room
    if (!room) return
    // The SDK times the pong itself; the artificial lag is added, not timed, so throttled
    // timers in a background tab cannot inflate the sample.
    const lag = this.opts.lagMs ?? 0
    this.out(() => room.ping((ms) => this.in(() => this.rtts.emit(ms + lag))))
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    this.retryTimer = null
    this.pingTimer = null
  }

  private patch(p: Partial<NetState>): void {
    this.state.set({ ...this.state.get(), ...p })
  }

  /** Outgoing traffic, delayed by half of `lagMs`. */
  private out(fn: () => void): void {
    this.delay(fn)
  }

  /** Incoming traffic, delayed by half of `lagMs`. */
  private in(fn: () => void): void {
    this.delay(fn)
  }

  private delay(fn: () => void): void {
    const half = (this.opts.lagMs ?? 0) / 2
    if (half > 0) setTimeout(fn, half)
    else fn()
  }
}
